import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Star, ArrowRight, ShieldCheck, MapPin, ClipboardList, UserRound, FlaskConical } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useCart } from "@/context/CartContext";
import { PLANS, FREEBIES, INGREDIENTS, WHO_FOR, GALLERY, FAQS, TRUST } from "@/data/content";

const Gallery = () => {
  const [active, setActive] = useState(0);
  return (
    <div className="lg:sticky lg:top-28">
      <div className="relative glass rounded-3xl overflow-hidden aspect-square grid place-items-center gold-glow">
        <div className="absolute inset-0 -z-10 grid place-items-center">
          <div className="h-2/3 w-2/3 rounded-full bg-gold/10 blur-[80px]" />
        </div>
        <AnimatePresence mode="wait">
          <motion.img
            key={active}
            src={GALLERY[active].src}
            alt={GALLERY[active].alt}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.4 }}
            className="w-full h-full object-contain p-5 drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
            data-testid="gallery-main-img"
          />
        </AnimatePresence>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {GALLERY.map((g, i) => (
          <button
            key={g.src}
            onClick={() => setActive(i)}
            data-testid={`gallery-thumb-${i}`}
            className={`glass rounded-xl aspect-square grid place-items-center transition-colors duration-300 ${
              active === i ? "border-gold" : "border-white/10 hover:border-white/25"
            }`}
          >
            <img src={g.src} alt={g.alt} className="w-3/4 h-3/4 object-contain" />
          </button>
        ))}
      </div>
    </div>
  );
};

const PriceSelector = ({ selected, setSelected }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {PLANS.map((plan) => {
      const best = plan.id === "triple";
      const isSel = selected === plan.id;
      return (
        <button
          key={plan.id}
          data-testid={`pp-offer-${plan.id}`}
          onClick={() => setSelected(plan.id)}
          className={`relative text-left rounded-2xl p-5 transition-colors duration-300 ${
            best ? "border border-gold/70 gold-glow bg-white/[0.03]" : "border border-white/10 hover:border-white/25 bg-white/[0.02]"
          } ${isSel ? "ring-1 ring-gold" : ""}`}
        >
          {best && (
            <span className="absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-obsidian">
              <Star className="h-3 w-3" fill="currentColor" /> {plan.badge}
            </span>
          )}
          <p className="text-xs uppercase tracking-[0.2em] text-vmuted">{plan.tagline}</p>
          <p className="font-serif text-2xl text-white mt-1">{plan.title}</p>
          <p className="text-xs text-vmuted mt-0.5">{plan.tabs} tablets · {plan.perTab}</p>
          <div className="mt-3 flex items-end justify-between">
            <p className="font-serif text-3xl text-white">₹{plan.price}</p>
            {plan.save && <p className="text-xs text-gold">{plan.save}</p>}
          </div>
          <span className={`absolute right-4 top-4 h-5 w-5 rounded-full border grid place-items-center transition-colors ${isSel ? "bg-gold border-gold" : "border-white/30"}`}>
            {isSel && <Check className="h-3 w-3 text-obsidian" strokeWidth={3} />}
          </span>
        </button>
      );
    })}
  </div>
);

export default function ProductPage() {
  const { addToCart } = useCart();
  const [selected, setSelected] = useState("triple");
  const plan = PLANS.find((p) => p.id === selected);

  return (
    <div className="App bg-obsidian">
      <Header />

      {/* Above the fold */}
      <section className="relative radial-emerald noise pt-28 md:pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10 lg:gap-16">
          <Gallery />

          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Veda Vitals</p>
              <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-white leading-[1.05]">
                ACV Effervescent Tablets
              </h1>
              <p className="mt-3 text-vmuted">20 Tablets per tube · Apple Cider Vinegar blend</p>
            </Reveal>

            <Reveal delay={0.06}>
              <div className="flex items-center gap-3 mt-5">
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-4 w-4 text-gold" fill="currentColor" />
                  ))}
                </div>
                <span className="text-sm text-vmuted">Loved by our community</span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-6">
                {TRUST.map((t) => (
                  <span key={t.label} className="inline-flex items-center gap-2 text-sm text-white/80">
                    <t.icon className="h-4 w-4 text-gold" strokeWidth={1.5} /> {t.label}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="mt-8">
                <PriceSelector selected={selected} setSelected={setSelected} />
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="glass rounded-2xl p-5 mt-5">
                <p className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Free with every order</p>
                <div className="grid grid-cols-2 gap-2">
                  {FREEBIES.map((f) => (
                    <span key={f} className="inline-flex items-center gap-2 text-sm text-white/80">
                      <Check className="h-4 w-4 text-gold" strokeWidth={2} /> {f}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.22}>
              <button
                data-testid="pp-buy-btn"
                onClick={() => addToCart(selected)}
                className="group mt-7 hidden sm:inline-flex items-center justify-center gap-2 rounded-full bg-gold px-9 py-4 text-obsidian font-medium hover:bg-gold-light transition-colors duration-300 w-full"
              >
                Add to Cart · ₹{plan.price}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Ingredients */}
      <section className="relative py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <Reveal><Overline index="01">Inside every tablet</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-white leading-[1.1]">
                A considered <span className="gold-text">blend.</span>
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {INGREDIENTS.map((ing, i) => (
              <motion.div
                key={ing.name}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                data-testid={`ingredient-${i}`}
                className="glass rounded-2xl p-7 hover:border-gold/30 transition-colors duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="h-11 w-11 rounded-xl border border-gold/25 grid place-items-center">
                    <FlaskConical className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  </div>
                  <span className="text-sm font-medium text-gold">{ing.dose}</span>
                </div>
                <h3 className="font-serif text-xl text-white mb-1">{ing.name}</h3>
                <p className="text-vmuted text-[15px] leading-relaxed">{ing.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="relative py-24 md:py-28 radial-emerald noise">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <Reveal><Overline index="02">Who is this for</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-white leading-[1.1]">
                Made for everyday adults.
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHO_FOR.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`whofor-${i}`}
                className="glass rounded-2xl p-7 hover:border-gold/30 transition-colors duration-300"
              >
                <div className="h-11 w-11 rounded-xl border border-gold/25 grid place-items-center mb-5">
                  <w.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl text-white mb-1">{w.title}</h3>
                <p className="text-vmuted text-[15px] leading-relaxed">{w.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal><Overline index="03">Product FAQ</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-white leading-[1.1] mb-12">
              Good to know.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="pp-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10" data-testid={`pp-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-serif text-xl md:text-2xl text-white hover:no-underline hover:text-gold py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-vmuted text-[15px] leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsAppButton bottomClass="bottom-24 sm:bottom-5" />

      {/* Sticky mobile Buy Now bar */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10 px-4 py-3 flex items-center justify-between gap-3" data-testid="sticky-buy-bar">
        <div>
          <p className="text-xs text-vmuted leading-none">{plan.title}</p>
          <p className="font-serif text-2xl text-white leading-tight">₹{plan.price}</p>
        </div>
        <button
          data-testid="pp-sticky-buy-btn"
          onClick={() => addToCart(selected)}
          className="flex-1 max-w-[60%] inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-obsidian font-medium"
        >
          Buy Now <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
